"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Compass, Layers, Search, TrendingUp, CircleCheck as CheckCircle2, ArrowRight, Sparkles, ShoppingBag, SlidersHorizontal as Sliders } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
export default function FashionStrategy() {
  const shouldReduceMotion = useReducedMotion();
  const strategicPillars = [{
    id: "pillar-1",
    number: "01",
    icon: Search,
    title: "Deep Keyword Research & Content Gap Analysis",
    subtitle: "High-Converting Intent Mapping",
    description: "I conducted exhaustive keyword research and content gap analysis to pinpoint high-converting, low-competition search terms mapped directly to buyer personas and customer journey stages.",
    deliverables: ["Identification of high-converting, low-competition keyword opportunities", "Search intent mapped across buyer personas and consideration stages", "Prioritised content gap matrix for key product and category hubs"]
  }, {
    id: "pillar-2",
    number: "02",
    icon: Sliders,
    title: "On-Page Optimization",
    subtitle: "Metadata & Content Structure",
    description: "I systematically overhauled on-page elements across product and category pages, rewriting titles, descriptions, and header hierarchies while structuring content for maximum readability and semantic depth.",
    deliverables: ["Rewritten product and category meta titles and meta descriptions", "Clear header hierarchy (H1, H2, H3) structured for search intent", "Enhanced content formatting for readability and topical relevance"]
  }, {
    id: "pillar-3",
    number: "03",
    icon: Layers,
    title: "Technical SEO Overhaul",
    subtitle: "Crawlability & Rich Results",
    description: "I resolved foundational technical barriers by fixing crawl errors, improving site architecture, boosting mobile performance, resolving Core Web Vitals issues, and deploying rich structured data.",
    deliverables: ["Resolved crawl errors and improved logical site architecture", "Enhanced mobile responsiveness and Core Web Vitals performance", "Implemented structured data schema for rich results (reviews, pricing, availability)"]
  }, {
    id: "pillar-4",
    number: "04",
    icon: TrendingUp,
    title: "Conversion Optimization",
    subtitle: "Internal Linking & Funnel Flow",
    description: "I connected organic traffic gains directly to commercial revenue by strengthening internal linking to high-value pages, refining calls-to-action, and streamlining the checkout funnel.",
    deliverables: ["Optimised internal linking distribution pointing to high-margin collection pages", "Refined call-to-action (CTA) hierarchy across category and product pages", "Simplified checkout pathway to reduce drop-off and friction points"]
  }];
  const milestones = [{
    label: "Phase 1: Deep Keyword Research & Content Gap Analysis",
    duration: "Months 1–2",
    summary: "Identified high-converting search queries, mapped buyer personas to journey stages, and pinpointed untapped keyword gaps."
  }, {
    label: "Phase 2: On-Page Optimization",
    duration: "Months 3–4",
    summary: "Rewrote metadata and headings across category and product templates, restructuring content for semantic relevance."
  }, {
    label: "Phase 3: Technical SEO Overhaul",
    duration: "Months 5–6",
    summary: "Resolved crawl errors, strengthened site architecture, tackled Core Web Vitals, and implemented structured data schema."
  }, {
    label: "Phase 4: Conversion Optimization",
    duration: "Months 7–8",
    summary: "Optimised internal link flow to high-value pages, refined CTAs, and simplified the checkout funnel for sustained conversion growth."
  }];
  return <section id="fashionretailerstrategy" data-nav="light" className="relative bg-background py-20 md:py-28 text-foreground overflow-hidden">
      <div className="absolute inset-0 pointer-events-none -z-10">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[720px] h-[400px] bg-secondary/30 rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-accent/10 rounded-full blur-2xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 font-medium bg-secondary text-secondary-foreground">
            <Compass className="size-3.5 text-primary" />
            <span>Strategic Framework</span>
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-default text-foreground mb-6">
            The Strategic Approach That Drove Sustainable Organic Growth
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            Every recommendation was tailored specifically to the ecommerce platform, margin structure, and competitive market dynamics. Here is the structured 4-phase methodology deployed to transform this fashion retailer&apos;s organic search presence.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {strategicPillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5,
            delay: i * 0.1,
            ease: "easeOut"
          }} className="h-full">
                <Card className="h-full border border-border/80 bg-card/60 backdrop-blur-xs shadow-md hover:shadow-lg transition-[border-color,box-shadow] duration-300 hover:border-primary/40 flex flex-col justify-between">
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                          <IconComponent className="size-5" />
                        </div>
                        <span className="text-xs font-mono font-semibold tracking-wider text-muted-foreground uppercase">
                          Phase {pillar.number}
                        </span>
                      </div>
                      <Badge variant="outline" className="text-xs font-normal border-border bg-background">
                        {pillar.subtitle}
                      </Badge>
                    </div>

                    <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                      {pillar.title}
                    </CardTitle>

                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-default">
                      {pillar.description}
                    </p>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <Separator className="mb-4" />
                    <div className="space-y-2.5">
                      <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                        Key Executions
                      </p>
                      <ul className="space-y-2">
                        {pillar.deliverables.map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-foreground/90 font-default">
                            <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-7 bg-card border border-border rounded-xl p-6 sm:p-8 shadow-md flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 text-primary mb-3">
                <ShoppingBag className="size-5" />
                <h3 className="text-xl font-bold font-default text-card-foreground">
                  Implementation Roadmap
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-6 font-default">
                A clear, sequential 8-month engagement roadmap delivering enduring technical, on-page, and conversion gains.
              </p>

              <div className="space-y-6">
                {milestones.map((step, idx) => <div key={step.label} data-index={idx} className="relative pl-6 border-l-2 border-primary/30 last:border-l-transparent pb-2">
                    <div className="absolute -left-[9px] top-0 size-4 rounded-full bg-primary border-4 border-background" />
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                      <span className="font-semibold text-sm sm:text-base font-default text-foreground">
                        {step.label}
                      </span>
                      <span className="text-xs font-mono text-primary font-medium bg-primary/10 px-2 py-0.5 rounded-md">
                        {step.duration}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-muted-foreground font-default">
                      {step.summary}
                    </p>
                  </div>)}
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 bg-primary text-primary-foreground rounded-xl p-6 sm:p-8 shadow-xl flex flex-col justify-between relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-8 -mr-8 w-48 h-48 bg-white/10 rounded-full blur-xl pointer-events-none" />

            <div className="relative z-10 space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-white/15 text-white text-xs font-medium backdrop-blur-xs">
                <Sparkles className="size-3.5" />
                <span>Strategic Execution</span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight font-default text-primary-foreground">
                End-to-End Strategy Execution
              </h3>

              <p className="text-sm sm:text-base text-primary-foreground/90 leading-relaxed font-default">
                Every phase was executed with hands-on technical precision—from deep crawl diagnostics and structured data deployment to taxonomy redesign and revenue-focused conversion pathways.
              </p>

              <div className="pt-2 space-y-2 text-xs sm:text-sm text-primary-foreground/80 font-default">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-secondary shrink-0" />
                  <span>Custom roadmap aligned with your inventory cycle</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-secondary shrink-0" />
                  <span>Bi-weekly strategic reviews & live implementation support</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-secondary shrink-0" />
                  <span>Transparent tracking tied directly to bottom-line sales</span>
                </div>
              </div>
            </div>

            <div className="pt-8 relative z-10 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-semibold shadow-sm">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-primary-foreground hover:bg-white/10 bg-transparent font-medium">
                <Link to="/CaseStudiesPage">
                  View All Case Studies
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}